import React, { useEffect, useState } from 'react';
import config from '../config';
import { Link, useParams } from 'react-router-dom';
import { fetchAuthSession } from 'aws-amplify/auth';

const ProductDetails = ({ testMode = false }) => {

    // const host = testMode ? config.testBackendUrl : config.backendUrl;
    const host = config.backendUrl;
    const productId = useParams().productId;

    const [product, setProduct] = useState(null);
    const [authToken, setAuthToken] = useState('');
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const getAuthToken = async () => {
            try {
                const session = await fetchAuthSession();
                const token = session.tokens.idToken.toString();
                setAuthToken(token);
            } catch (error) {
                console.error('Error getting auth token:', error);
            }
        };
        
        getAuthToken();
    }, []);


    useEffect(() => {
        if (!authToken || !productId) return; // Don't fetch if no token or productId yet

        setLoading(true);
        fetch(`${host}/api/products/${productId}`, {
            headers: {
                "X-API-KEY": process.env.REACT_APP_GANDF_KEY,
                'Content-Type': 'application/json',
            }
        })
        .then(response => {
            console.log(productId);
            console.log(process.env.REACT_APP_GANDF_KEY);
            if (!response.ok) {
                console.error('Network response was not ok', response);
            }
            return response.json();
        })
        .then(data => {
            setProduct(data);
            setLoading(false);
        })
        .catch(error => {
            console.error('Error fetching product details:', error);
            setLoading(false);
        });
    }, [authToken, productId]);
    
    if (loading) {
        return (
            <div className="main-content">
                <div style={{ textAlign: 'center', padding: 'var(--spacing-3xl)' }}>
                    <div className="spinner" style={{ margin: '0 auto' }}></div>
                    <p style={{ marginTop: 'var(--spacing-md)', color: 'var(--text-secondary)' }}>
                        Loading product details...
                    </p>
                </div>
            </div>
        );
    }

    if (!product) {
        return (
            <div className="main-content">
                <div className="card">
                    <div className="card-body" style={{ textAlign: 'center', padding: 'var(--spacing-3xl)' }}>
                        <div style={{ fontSize: '3rem', marginBottom: 'var(--spacing-lg)' }}>❌</div>
                        <h3 style={{ marginBottom: 'var(--spacing-md)' }}>Product Not Found</h3>
                        <p style={{ color: 'var(--text-secondary)' }}>
                            The product you're looking for doesn't exist or has been removed.
                        </p>
                        <Link to={testMode ? "/test/products" : "/products"} className="btn btn-primary">
                            Back to Products
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="main-content">
            {/* Page Header */}
            <div className="page-header">
                <div className="page-header-left">
                    <h1 className="page-header-title">
                        Product Details
                        {testMode && <span style={{color: 'var(--warning-color)'}}> (Test Mode)</span>}
                    </h1>
                </div>
                <div className="page-header-actions">
                    <Link to={testMode ? "/test/products" : "/products"} className="btn btn-secondary">
                        ← Back to Products
                    </Link>
                    <Link to={`/edit-product/${product.id}`} className="btn btn-primary">
                        ✏️ Edit Product
                    </Link>
                </div>
            </div>

            {/* Product Information */}
            <div className="card">
                <div className="card-header">
                    <h3 className="card-title">Product Information</h3>
                </div>
                <div className="card-body">
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 'var(--spacing-lg)' }}>
                        <div>
                            <div style={{ marginBottom: 'var(--spacing-lg)' }}>
                                <label className="form-label">Product ID</label>
                                <div style={{ 
                                    fontFamily: 'var(--font-family-mono)', 
                                    fontWeight: 'var(--font-weight-medium)',
                                    padding: 'var(--spacing-sm)',
                                    backgroundColor: 'var(--bg-secondary)',
                                    borderRadius: 'var(--radius-sm)',
                                    border: '1px solid var(--border-light)'
                                }}>
                                    {product.id}
                                </div>
                            </div>
                            
                            <div style={{ marginBottom: 'var(--spacing-lg)' }}>
                                <label className="form-label">Product Name</label>
                                <div style={{ 
                                    fontWeight: 'var(--font-weight-semibold)',
                                    padding: 'var(--spacing-sm)',
                                    backgroundColor: 'var(--bg-secondary)',
                                    borderRadius: 'var(--radius-sm)',
                                    border: '1px solid var(--border-light)'
                                }}>
                                    {product.name}
                                </div>
                            </div>
                        </div>
                        
                        <div>
                            <div style={{ marginBottom: 'var(--spacing-lg)' }}>
                                <label className="form-label">Price</label>
                                <div style={{ 
                                    fontSize: 'var(--font-size-xl)',
                                    fontWeight: 'var(--font-weight-bold)',
                                    color: 'var(--success-color)',
                                    padding: 'var(--spacing-sm)',
                                    backgroundColor: 'var(--success-light)',
                                    borderRadius: 'var(--radius-sm)',
                                    border: '1px solid var(--success-color)'
                                }}>
                                    ${product.price.toFixed(2)}
                                </div>
                            </div>
                            
                            <div style={{ marginBottom: 'var(--spacing-lg)' }}>
                                <label className="form-label">Status</label>
                                <div>
                                    <span className="badge badge-success">Active</span>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    <div style={{ marginTop: 'var(--spacing-lg)' }}>
                        <label className="form-label">Description</label>
                        <div style={{ 
                            padding: 'var(--spacing-md)',
                            backgroundColor: 'var(--bg-secondary)',
                            borderRadius: 'var(--radius-md)',
                            border: '1px solid var(--border-light)',
                            minHeight: '100px',
                            lineHeight: 'var(--line-height-relaxed)'
                        }}>
                            {product.description || 'No description available'}
                        </div>
                    </div>
                </div>
            </div>

            {/* Quick Actions */}
            <div className="card">
                <div className="card-header">
                    <h3 className="card-title">Quick Actions</h3>
                </div>
                <div className="card-body">
                    <div style={{ display: 'flex', gap: 'var(--spacing-md)', flexWrap: 'wrap' }}>
                        <Link to={testMode ? `/test/book-builder/${product.id}` : `/book-builder/${product.id}`} className="btn btn-primary">
                            ✏️ Book Builder
                        </Link>
                        <button className="btn btn-danger">
                            🗑️ Delete Product
                        </button>
                        <button className="btn btn-secondary">
                            📊 View Analytics
                        </button>
                        <button className="btn btn-ghost">
                            📋 View Orders
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ProductDetails;